// src/pages/Dashboard.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAllBalances } from "@/hooks/use-all-balances";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { TokenLogo } from "@/components/TokenLogo";

// ========== Animation Styles ==========
const animationStyles = `
  @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes spin-slow-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
  @keyframes spin-slower { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes pulse-logo { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
  .animate-spin-slow { animation: spin-slow 8s linear infinite; }
  .animate-spin-slow-reverse { animation: spin-slow-reverse 6s linear infinite; }
  .animate-spin-slower { animation: spin-slower 10s linear infinite; }
  .animate-pulse-logo { animation: pulse-logo 2s ease-in-out infinite; }
`;

interface Token {
  symbol: string;
  price: number;
  change24h: number;
  name?: string;
  icon?: string;
}

const INITIAL_TOKENS: Token[] = [
  { symbol: "BTC", price: 0, change24h: 0, name: "BTCUSDT" },
  { symbol: "ETH", price: 0, change24h: 0, name: "ETHUSDT" },
  { symbol: "BNB", price: 0, change24h: 0, name: "BNBUSDT" },
  { symbol: "LINK", price: 0, change24h: 0, name: "LINKUSDT" },
  { symbol: "CAKE", price: 0, change24h: 0, name: "CAKEUSDT" },
  { symbol: "XRP", price: 0, change24h: 0, name: "XRPUSDT" },
  { symbol: "BONK", price: 0, change24h: 0, name: "BONKUSDT" },
  { symbol: "SHIB", price:0, change24h: 0, name: "SHIBUSDT" },
  { symbol: "SOL", price: 0, change24h: 0, name: "SOLUSDT" },
  { symbol: "USDT", price: 0, change24h: 0, name: "USDTUSDT" },
  { symbol: "FIXERCOIN", price: 0, change24h: 0, name: "FIXERCOINUSDT" },
  { symbol: "PINGX", price: 0, change24h: 0, name: "PINGXUSDT" },
  { symbol: "LOCKER", price: 0, change24h: 0, name: "LOCKERUSDT" },
  { symbol: "FXM", price: 0, change24h: 0, name: "FXMUSDT" },
];

const TOKEN_ADDRESSES = {
  BTC: { chain: "ethereum", address: "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599" },
  ETH: { chain: "ethereum", address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2" },
  BNB: { chain: "bsc", address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c" },
  LINK: { chain: "ethereum", address: "0x514910771af9ca656af840dff83e8264ecf986ca" },
  CAKE: { chain: "bsc", address: "0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82" },
  XRP: { chain: "bsc", address: "0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dbe" },
  SOL: { chain: "solana", address: "So11111111111111111111111111111111111111112" },
  BONK: { chain: "solana", address: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263" },
  SHIB: { CHAIN: "ethereum", address: "0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE" },
  USDT: { chain: "solana", address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB" },
  FIXERCOIN: { chain: "solana", address: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump" },
  PINGX: { chain: "solana", address: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump" },
  LOCKER: { chain: "solana", address: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump" },
  FXM: { chain: "solana", address: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump" },
};

const HOT_TOKEN_SYMBOLS = [
  "BTC", "ETH", "BNB", "LINK", "CAKE", "XRP", "SOL", "BONK","SHIB",
  "FIXERCOIN", "PINGX", "LOCKER", "FXM",
] as const;
const HOT_TOKEN_SET = new Set<string>(HOT_TOKEN_SYMBOLS);

const formatLargeNumber = (num: number): string => {
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  return `${num.toFixed(0)}`;
};

// ========== Currency Selector ==========
const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US DOLLAR" },
  { code: "PKR", symbol: "₨", label: "PAKISTANI RUPEE" },
  { code: "EUR", symbol: "€", label: "EURO" },
  { code: "GBP", symbol: "£", label: "BRITISH POUND" },
  { code: "INR", symbol: "₹", label: "INDIAN RUPEE" },
  { code: "AED", symbol: "د.إ", label: "UAE DIRHAM" },
  { code: "SAR", symbol: "﷼", label: "SAUDI RIYAL" },
  { code: "JPY", symbol: "¥", label: "JAPANESE YEN" },
  { code: "CNY", symbol: "¥", label: "CHINESE YUAN" },
  { code: "BTC", symbol: "₿", label: "BITCOIN" },
] as const;

const FALLBACK_FX: Record<string, number> = {
  USD: 1,
  PKR: 278,
  EUR: 0.92,
  GBP: 0.79,
  INR: 83,
  AED: 3.67,
  SAR: 3.75,
  JPY: 150,
  CNY: 7.2,
  BTC: 1 / 65000,
};

const BTC_ADDRESS = "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599";

const convertValue = (
  usdValue: number,
  currencyCode: string,
  prices?: Record<string, any>
): number => {
  if (!usdValue) return 0;
  if (currencyCode === "USD") return usdValue;
  if (currencyCode === "BTC") {
    const btcPrice = prices?.[BTC_ADDRESS]?.priceUsd || 65000;
    return usdValue / btcPrice;
  }
  return usdValue * (FALLBACK_FX[currencyCode] || 1);
};

// No symbol prefix — currency is shown in the label, not on the number
const formatCurrencyValue = (value: number, currencyCode: string): string => {
  if (currencyCode === "BTC") return value.toFixed(8);
  if (currencyCode === "PKR" || currencyCode === "INR" || currencyCode === "JPY") {
    return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ========== Left Column (Desktop) ==========
// ========== Left Column (Desktop) — news + apply section ==========
interface NewsArticle {
  id: string;
  title: string;
  body: string;
  url: string;
  imageurl: string;
  source: string;
  source_info?: { name?: string };
  categories: string;
  published_on: number;
}

const ShortcutDetails = ({ userEmail }: { userEmail?: string }) => {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchNews = async () => {
      try {
        // Inside ShortcutDetails, replace the fetch URL:
const res = await fetch("/api/news"); // <-- Use your new proxy
        if (!res.ok) throw new Error(`news HTTP ${res.status}`);
        const json = await res.json();
        const items: NewsArticle[] = (json?.Data || []).slice(0, 5);
        if (!cancelled) {
          setNews(items);
          setNewsError(false);
        }
      } catch (err) {
        console.warn("[News] fetch failed:", err);
        if (!cancelled) setNewsError(true);
      } finally {
        if (!cancelled) setNewsLoading(false);
      }
    };
    fetchNews();
    const t = setInterval(fetchNews, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const timeAgo = (ts: number) => {
    const diff = Math.max(0, Math.floor(Date.now() / 1000 - ts));
    if (diff < 60) return `${diff}S AGO`;
    if (diff < 3600) return `${Math.floor(diff / 60)}M AGO`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}H AGO`;
    return `${Math.floor(diff / 86400)}D AGO`;
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
          <span className="text-black text-[11px] font-bold">→</span>
        </div>
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-yellow-400">
          Most Relevant News
        </h2>
      </div>

      {/* Account row (small, above news) */}
      <div className="mb-4 space-y-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Your Account
        </p>
        <p className="text-[11px] font-mono text-foreground break-all">
          {userEmail || "Not logged in"}
        </p>
      </div>

      <div className="border-t border-border mb-4"></div>

      {/* News list */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-3">
        {newsLoading && (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!newsLoading && newsError && news.length === 0 && (
          <p className="text-[11px] text-muted-foreground uppercase text-center py-8">
            News unavailable right now. Please try again later.
          </p>
        )}

        {!newsLoading &&
          !newsError &&
          news.map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg border border-border hover:border-yellow-400/60 transition-colors overflow-hidden bg-background"
            >
              <div className="flex gap-3">
                {article.imageurl && (
                  <img
                    src={article.imageurl}
                    alt={article.title}
                    className="w-20 h-20 object-cover flex-shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <div className="p-2 min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[9px] uppercase text-yellow-400 font-semibold mb-1">
                    <span className="truncate">
                      {article.categories?.split("|")[0] || "CRYPTO"}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      {timeAgo(article.published_on)}
                    </span>
                  </div>
                  <p className="text-[11px] font-semibold text-foreground leading-tight line-clamp-2">
                    {article.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-snug line-clamp-2 mt-1">
                    {article.body}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[9px] uppercase text-muted-foreground truncate">
                      {article.source_info?.name || article.source || "NEWS"}
                    </span>
                    <span className="text-[9px] uppercase text-yellow-400 font-semibold">
                      Read more →
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
      </div>

      <div className="border-t border-border my-3"></div>

      {/* Upcoming gem + apply (kept from original) */}
      <p className="text-[11px] text-muted-foreground text-center uppercase tracking-wider">
        UPCOMING NEW GEM AT FIXORIUM
      </p>
      <div className="flex justify-center pt-2">
        <button
          onClick={() => navigate("/apply")}
          className="text-[12px] uppercase text-yellow-400 hover:text-yellow-500 transition-colors font-semibold"
        >
          Apply for Token Listing →
        </button>
      </div>



      
    </div>
  );
};

// ========== Price Marquee ==========
const PriceMarquee = ({ prices }: { prices: Record<string, any> }) => {
  const [marqueeTokens, setMarqueeTokens] = useState<
    { symbol: string; name: string; price: number; change: number }[]
  >([]);
  useEffect(() => {
    const excluded = new Set(["FIXERCOIN", "LOCKER", "FXM", "PINGX", "USDT", "USDC"]);
    const tokenNames: Record<string, string> = {
      BTC: "bitcoin", ETH: "ethereum", BNB: "bnb", SOL: "solana",
      XRP: "XRP", BONK: "solana", LINK: "chainlink", CAKE: "pancakeswap",
    };
    const tokens = Object.keys(TOKEN_ADDRESSES).filter((sym) => !excluded.has(sym));
    const tokenPrices = tokens
      .map((symbol) => {
        const addr = TOKEN_ADDRESSES[symbol as keyof typeof TOKEN_ADDRESSES]?.address;
        const priceData = addr ? prices[addr] : null;
        const price = priceData?.priceUsd || 0;
        const change = priceData?.priceChange24h || 0;
        return { symbol, name: tokenNames[symbol] || symbol.toLowerCase(), price, change };
      })
      .filter((item) => item.price > 0);
    setMarqueeTokens(tokenPrices);
  }, [prices]);
  if (marqueeTokens.length === 0) return null;
  const loopTokens = [...marqueeTokens, ...marqueeTokens];
  return (
<div className="overflow-hidden whitespace-nowrap border-b border-border pt-0 pb-2">
  <div className="inline-block animate-marquee-desktop">
        {loopTokens.map((token, idx) => (
          <div key={idx} className="inline-flex items-center mx-4 text-[11px] gap-2">
            <span className="text-muted-foreground uppercase">{token.symbol}</span>
            <span className="text-muted-foreground uppercase">{token.name}</span>
            <span className="text-foreground font-semibold">
              {token.price.toFixed(5).replace(/\.?0+$/, "")}
            </span>
            <span className={`font-semibold ${token.change >= 0 ? "text-green-500" : "text-red-500"}`}>
              {token.change >= 0 ? "+" : ""}
              {token.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-desktop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-desktop { animation: marquee-desktop 30s linear infinite; }
      `}</style>
    </div>
  );
};

// ========== Announcement Marquee ==========
const AnnouncementMarquee = () => {
  const messages = [
    "FIXORIUM IS EXPANDING — FIXERCOIN, PINGX, LOCKER & FXM ARE NOW SUPPORTED",
    "TRADE FIXERCOIN, PINGX, LOCKER, FXM — NEW LISTINGS LIVE ON FIXORIUM",
    "BIG NEWS: FIXORIUM NOW SUPPORTS FIXERCOIN, PINGX, LOCKER, FXM",
    "THE NEXT GENERATION OF TOKENS HAS LANDED ON FIXORIUM EXCHANGE",
  ];
  const loopItems: string[] = [];
  for (let i = 0; i < 4; i++) loopItems.push(...messages);
  return (
<div className="overflow-hidden whitespace-nowrap border-b border-border pt-1 pb-2 mb-2">
  <div className="inline-block animate-marquee-announcement">
        {loopItems.map((msg, idx) => (
          <span
            key={idx}
            className="inline-flex items-center mx-6 text-[11px] uppercase font-semibold tracking-wider text-muted-foreground"
          >
            {msg}
            <span className="ml-6 text-muted-foreground/60">•</span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes marquee-announcement { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-announcement { animation: marquee-announcement 45s linear infinite; }
      `}</style>
    </div>
  );
};

// ========== Token Card ==========
const TokenCard = ({ token, isFavorite, onTokenClick, currency, prices }: any) => {
  const isPos = token.change24h >= 0;
  const displayName = token.name || `${token.symbol}USDT`;
  const cur = currency || "USD";

  const convertedPrice =
    token.price > 0 ? convertValue(token.price, cur, prices) : 0;

  // No symbol prefix — the column header carries the currency
  const priceDisplay =
    token.price <= 0
      ? "---"
      : cur === "BTC"
      ? convertedPrice.toFixed(8)
      : cur === "PKR" || cur === "INR" || cur === "JPY"
      ? convertedPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : convertedPrice.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        });

  return (
    <div
      className="w-full grid grid-cols-3 gap-4 py-3 rounded-lg hover:bg-muted transition-colors text-left items-center cursor-pointer"
      onClick={() => onTokenClick(token)}
    >
      <div className="flex items-center gap-2">
        <TokenLogo symbol={token.symbol} size="sm" />
        <span className="font-semibold text-[11px] uppercase text-foreground">{displayName}</span>
      </div>
      <div className="text-right text-[11px] text-foreground">{priceDisplay}</div>
      <div className="text-right font-semibold text-[13px]">
        {token.price > 0 ? (
          <span
            className={`px-3 py-1.5 rounded text-[13px] font-bold ${
              isPos ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"
            }`}
          >
            {isPos ? "+" : ""}
            {token.change24h.toFixed(2)}%
          </span>
        ) : (
          <span className="px-3 py-1.5 rounded text-[13px] font-bold text-muted-foreground">---</span>
        )}
      </div>
    </div>
  );
};

const formatPrice = (t: Token) => {
  if (t.price <= 0) return "---";
  if (["BTC", "ETH", "BNB", "SOL", "USDT", "USDC", "XRP", "LINK", "CAKE"].includes(t.symbol))
    return `$${t.price.toFixed(2)}`;
  if (t.symbol === "MATIC") return `$${t.price.toFixed(4)}`;
  if (t.price < 0.001) return `$${t.price.toFixed(6)}`;
  if (t.price < 1) return `$${t.price.toFixed(4)}`;
  return `$${t.price.toFixed(2)}`;
};

// ========== Right Column Component ==========
const DashboardContent = ({
  isDesktop,
  totalBalance,
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  displayTokens,
  initialDisplay,
  showAllTokens,
  setShowAllTokens,
  hasMore,
  favorites,
  onTokenClick,
  onDepositClick,
  onConnectWallet,
  prices,
}: any) => {
  const [currency, setCurrency] = useState<string>("USD");
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("dashboard_currency");
    if (stored && CURRENCIES.some((c) => c.code === stored)) {
      setCurrency(stored);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("dashboard_currency", currency);
  }, [currency]);

  useEffect(() => {
    if (!showCurrencyMenu) return;
    const handler = () => setShowCurrencyMenu(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [showCurrencyMenu]);

  const convertedTotal = convertValue(totalBalance, currency, prices);
  const activeCurrency = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  return (
    <div
      className={
        isDesktop
          ? "bg-card rounded-xl p-6 border border-border"
          : "bg-transparent border-0 px-0 py-6"
      }
    >
      <div className="space-y-6 mb-8">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div
              className={`${
                isDesktop ? "text-[14px]" : "text-sm"
              } text-muted-foreground tracking-wide uppercase`}
            >
              TOTAL BALANCE ({currency})
            </div>

            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setShowCurrencyMenu((v) => !v)}
                className="flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors"
                aria-label="Select currency"
                title={`Currency: ${activeCurrency.code}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-foreground"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span className="text-[10px] font-semibold uppercase text-foreground">
                  {currency}
                </span>
              </button>

              {showCurrencyMenu && (
                <div className="absolute right-0 mt-1 w-44 max-h-64 overflow-y-auto bg-card border border-border rounded-md shadow-lg z-50">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => {
                        setCurrency(c.code);
                        setShowCurrencyMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-[11px] uppercase transition-colors hover:bg-muted ${
                        c.code === currency
                          ? "text-yellow-400 font-semibold"
                          : "text-foreground"
                      }`}
                    >
                      <span className="font-bold">{c.code}</span>
                      <span className="ml-2 text-muted-foreground">{c.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="text-5xl font-bold text-foreground">
            {formatCurrencyValue(convertedTotal, currency)}
          </div>
        </div>

        <Button
          onClick={onDepositClick}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold h-10 uppercase"
        >
          Deposit
        </Button>
      </div>

      <div className="w-full">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4 gap-1 bg-muted uppercase">
            <TabsTrigger value="favorites" className="text-[11px] uppercase">Fav</TabsTrigger>
            <TabsTrigger value="hot" className="text-[11px] uppercase">Hot</TabsTrigger>
            <TabsTrigger value="gainers" className="text-[11px] uppercase">Gainers</TabsTrigger>
            <TabsTrigger value="losers" className="text-[11px] uppercase">Losers</TabsTrigger>
          </TabsList>
          <TabsContent value={activeTab} className="mt-6 space-y-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="SEARCH TOKENS..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 uppercase text-[11px]"
              />
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-4 py-2 text-[9px] text-muted-foreground font-semibold uppercase">
                <div>NAME</div>
                <div className="text-right">LAST PRICE ({currency})</div>
                <div className="text-right">24H CHANGE</div>
              </div>
              {displayTokens.length > 0 ? (
                <>
                  {(showAllTokens ? displayTokens : initialDisplay).map((t: Token) => (
                    <TokenCard
                      key={t.symbol}
                      token={t}
                      isFavorite={favorites.includes(t.symbol)}
                      onTokenClick={onTokenClick}
                      currency={currency}
                      prices={prices}
                    />
                  ))}
                  {hasMore && (
                    <button
                      onClick={() => setShowAllTokens(!showAllTokens)}
                      className="w-full mt-6 py-3 text-[9px] uppercase text-yellow-400 hover:text-yellow-500 flex items-center justify-center gap-2 transition-colors border-t border-border pt-4"
                    >
                      {showAllTokens ? (
                        <>
                          <ChevronUp className="w-3 h-3" /> Show Less Tokens
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" /> Show More Tokens (
                          {displayTokens.length - 5} more)
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground uppercase text-[10px]">
                  {activeTab === "favorites"
                    ? "NO FAV TOKENS YET. ADD SOME BY CLICKING THE STAR!"
                    : "NO TOKENS FOUND"}
                </div>
              )}
            </div>
            <div className="text-right pt-4">
              <Button
                variant="ghost"
                className="text-yellow-400 hover:text-yellow-500 uppercase text-[10px]"
                onClick={onConnectWallet}
              >
                connect wallet →
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ========== Main Dashboard Component ==========
export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState("hot");
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState<string[]>(["FXM", "PINGX", "LOCKER", "FIXERCOIN"]);
  const [tokenBalances, setTokenBalances] = useState<Record<string, number>>({});
  const [tokens, setTokens] = useState<Token[]>(INITIAL_TOKENS);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [showAllTokens, setShowAllTokens] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch("/api/auth", { method: "POST", credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.user) setUser(data.user);
          else navigate("/login");
        } else navigate("/login");
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("dashboardFavorites");
    if (stored) try { setFavorites(JSON.parse(stored)); } catch (e) {}
  }, []);
  useEffect(() => {
    localStorage.setItem("dashboardFavorites", JSON.stringify(favorites));
  }, [favorites]);

  const solanaWalletAddress = user?.wallets?.solana || "";

  const { solanaBalances, evmBalances } = useAllBalances({
    solanaWalletAddress,
    userId: user?.email || null,
    refetchInterval: 15000,
    enabled: !!user?.email,
  });

  useEffect(() => {
    if (solanaBalances || evmBalances) {
      setTokenBalances({
        BTC: evmBalances?.ethereum?.BTC || 0,
        ETH: evmBalances?.ethereum?.ETH || 0,
        BNB: evmBalances?.bsc?.BNB || 0,
        LINK: evmBalances?.ethereum?.LINK || 0,
        CAKE: evmBalances?.bsc?.CAKE || 0,
        XRP: evmBalances?.bsc?.CAKE || 0,
        SOL: solanaBalances?.SOL || 0,
        USDT: solanaBalances?.USDT || 0,
        FIXERCOIN: solanaBalances?.FIXERCOIN || 0,
        PINGX: solanaBalances?.PINGX || 0,
        LOCKER: solanaBalances?.LOCKER || 0,
        FXM: solanaBalances?.FXM || 0,
      });
    }
  }, [solanaBalances, evmBalances]);

  const { prices, loading: pricesLoading } = useMultipleTokenPrices(
    Object.values(TOKEN_ADDRESSES).map((t) => t.address),
    { refetchInterval: 10000 }
  );

  useEffect(() => {
    if (prices && Object.keys(prices).length) {
      setTokens((prev) =>
        prev.map((t) => {
          if (t.symbol === "USDT" || t.symbol === "USDC") return { ...t, price: 1, change24h: 0 };
          const info = TOKEN_ADDRESSES[t.symbol as keyof typeof TOKEN_ADDRESSES];
          if (!info) return t;
          const data = prices[info.address];
          if (data && data.priceUsd > 0) {
            return { ...t, price: data.priceUsd, change24h: data.priceChange24h || 0 };
          }
          return { ...t, price: 0, change24h: 0 };
        })
      );
    }
  }, [prices]);

  const toggleFavorite = (symbol: string) =>
    setFavorites((prev) =>
      prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]
    );

  const handleTokenClick = (token: Token) => {
    console.log("Token clicked:", token.symbol);
    localStorage.setItem("chart_selected_token", token.symbol);
    navigate(`/chart?token=${token.symbol}`);
  };

  const handleDepositClick = () => {
    console.log("Deposit button clicked");
    navigate("/deposit");
  };

  const handleConnectWallet = () => {
    console.log("Connect wallet clicked");
    navigate("/web3");
  };

  if (loading)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground">Loading dashboard...</p>
        </div>
      </div>
    );

  const matchesSearch = (t: Token) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      t.symbol.toLowerCase().includes(searchLower) ||
      (t.name && t.name.toLowerCase().includes(searchLower))
    );
  };
  const filteredTokens = tokens.filter(matchesSearch);
  const favoriteTokens = tokens.filter((t) => favorites.includes(t.symbol));
  const hotTokens = tokens.filter((t) => HOT_TOKEN_SET.has(t.symbol)).filter(matchesSearch);
  const gainers = hotTokens.filter((t) => t.change24h > 0).sort((a, b) => b.change24h - a.change24h);
  const losers = hotTokens.filter((t) => t.change24h < 0).sort((a, b) => a.change24h - b.change24h);
  const getDisplay = () => {
    if (activeTab === "favorites") return favoriteTokens.filter(matchesSearch);
    if (activeTab === "hot") return hotTokens;
    if (activeTab === "gainers") return gainers;
    if (activeTab === "losers") return losers;
    return filteredTokens;
  };
  const displayTokens = getDisplay();
  const initialDisplay = displayTokens.slice(0, 5);
  const hasMore = displayTokens.length > 5;

  const totalBalance = Object.entries(tokenBalances).reduce((sum, [sym, amt]) => {
    const t = tokens.find((t) => t.symbol === sym);
    return t && t.price > 0 ? sum + amt * t.price : sum;
  }, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <style>{animationStyles}</style>
      {isDesktop ? (
        <>
          <PriceMarquee prices={prices} />
          <AnnouncementMarquee />
          <div className="flex gap-[3px] px-6 pb-6 min-h-[calc(100vh-40px)]">
            <div className="w-1/2">
              <ShortcutDetails userEmail={user?.email} />
            </div>
            <div className="w-1/2">
              <DashboardContent
                isDesktop={isDesktop}
                totalBalance={totalBalance}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                displayTokens={displayTokens}
                initialDisplay={initialDisplay}
                showAllTokens={showAllTokens}
                setShowAllTokens={setShowAllTokens}
                hasMore={hasMore}
                favorites={favorites}
                onTokenClick={handleTokenClick}
                onDepositClick={handleDepositClick}
                onConnectWallet={handleConnectWallet}
                prices={prices}
              />
            </div>
          </div>
        </>
      ) : (
        // MOBILE VIEW — no AnnouncementMarquee
<div className="px-[14px] pt-0 pb-2">
  <DashboardContent
            isDesktop={isDesktop}
            totalBalance={totalBalance}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            displayTokens={displayTokens}
            initialDisplay={initialDisplay}
            showAllTokens={showAllTokens}
            setShowAllTokens={setShowAllTokens}
            hasMore={hasMore}
            favorites={favorites}
            onTokenClick={handleTokenClick}
            onDepositClick={handleDepositClick}
            onConnectWallet={handleConnectWallet}
            prices={prices}
          />
        </div>
      )}
    </div>
  );
}
